/**
 * FeedbackPanel - AI Radar feedback toggles in editor toolbar area.
 *
 * Per feature_inventory.md Q1 & Q2 decisions:
 * - Collapsed: Single radar icon (📡) - the "Songwriting Radar" brand
 * - Expanded: Shows 3-4 primary toggles inline (stress, rhyme, section, stability)
 * - Advanced: Future "More settings..." link to full modal
 *
 * Current scope (07): Stress toggle only
 * Future scopes: Rhyme (13), Section Validation (14), Line Stability (15)
 *
 * This is for USER PREFERENCES (feedback display), not song-specific settings.
 */

import { useState } from 'react'
import { useEditorPreferences } from '../../hooks/useEditorPreferences'
import RhymeLegend from './components/RhymeLegend'

/**
 * SVG Icons - inline SVGs render consistently across all browsers.
 * Emoji characters can fail to render depending on OS/browser font support.
 */

// Radar/satellite dish icon - the "AI Radar" brand icon
function RadarIcon({ className = 'w-4 h-4' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="2" />
      <path d="M16.24 7.76a6 6 0 0 1 0 8.49m-8.48-.01a6 6 0 0 1 0-8.49m11.31-2.82a10 10 0 0 1 0 14.14m-14.14 0a10 10 0 0 1 0-14.14" />
    </svg>
  )
}

// Stress mark icon - represents syllable stress patterns
function StressMarkIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      {/* Accent mark over a letter shape */}
      <path d="M12 4L14 8H10L12 4Z" /> {/* Acute accent */}
      <path d="M8 20V12C8 10.9 8.9 10 10 10H14C15.1 10 16 10.9 16 12V20H14V12H10V20H8Z" /> {/* Letter base */}
    </svg>
  )
}

// Stress count icon - represents numerical count badge
function StressCountIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {/* Badge/pill shape with number inside */}
      <rect x="3" y="8" width="18" height="8" rx="4" />
      <path d="M10 11v4m2-4l0.7 2.8L14 11v4m-1.5-2h1" /> {/* "12" stylized */}
    </svg>
  )
}

// Rhyme label icon - letters A/B representing rhyme scheme
function RhymeLabelIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <text x="4" y="14" fontSize="10" fontWeight="bold">A</text>
      <text x="13" y="18" fontSize="8" fontWeight="bold">B</text>
    </svg>
  )
}

// Section validation icon - grid/structure icon representing section validation
function SectionValidationIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {/* Grid/structure representing sections */}
      <rect x="3" y="3" width="7" height="5" rx="1" />
      <rect x="3" y="10" width="7" height="5" rx="1" />
      <rect x="3" y="17" width="7" height="4" rx="1" />
      <path d="M12 5h9m-9 6h9m-9 6h9" strokeLinecap="round" />
    </svg>
  )
}

// Line stability icon - even/odd line count indicator
function LineStabilityIcon({ className = 'w-3.5 h-3.5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {/* Two stacked lines (even) */}
      <path d="M4 8h16M4 16h16" strokeLinecap="round" />
      {/* Circle indicator */}
      <circle cx="20" cy="12" r="2" fill="currentColor" stroke="none" />
    </svg>
  )
}

/**
 * Compact toggle switch with icon - Q2 hybrid design.
 * Switch mechanism + icon + label for clear state indication.
 */
interface CompactToggleProps {
  enabled: boolean
  onToggle: () => void
  label: string
  icon: React.ReactNode
  testId: string
}

function CompactToggle({ enabled, onToggle, label, icon, testId }: CompactToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={enabled ? `Disable ${label}` : `Enable ${label}`}
      data-testid={testId}
      onClick={onToggle}
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium
        transition-all duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
        ${enabled
          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          : 'bg-neutral-100 text-neutral-500 hover:bg-neutral-200'
        }
      `}
      title={enabled ? `${label} enabled - click to disable` : `${label} disabled - click to enable`}
    >
      {/* Icon */}
      <span className={enabled ? 'text-blue-600' : 'text-neutral-400'}>
        {icon}
      </span>
      {/* Switch track with knob */}
      <span className={`
        relative inline-flex w-7 h-4 rounded-full transition-colors duration-200
        ${enabled ? 'bg-blue-500' : 'bg-neutral-300'}
      `}>
        <span className={`
          absolute top-0.5 left-0.5 w-3 h-3 rounded-full bg-white shadow-sm
          transition-transform duration-200
          ${enabled ? 'translate-x-3' : 'translate-x-0'}
        `} />
      </span>
      {/* Label - always visible for clarity */}
      <span>{label}</span>
    </button>
  )
}

/**
 * FeedbackPanel - The collapsible panel containing feedback toggles.
 *
 * Design from feature_inventory.md:
 * - Collapsed: Single radar icon button
 * - Expanded: Row of compact toggles
 * - Shows rhyme legend when rhyme labels are enabled
 */
export default function FeedbackPanel() {
  const [isExpanded, setIsExpanded] = useState(true) // Default expanded for discoverability
  const [showLegend, setShowLegend] = useState(false) // Legend collapsed by default to reduce clutter
  const { preferences, updatePreferences } = useEditorPreferences()

  const stressEnabled = preferences.feedback.stress.enabled
  const stressCountEnabled = preferences.feedback.stress.showStressCount
  const rhymeLabelsEnabled = preferences.feedback.rhyme.showLabels
  const sectionValidationEnabled = preferences.feedback.section.showValidation
  const lineStabilityEnabled = preferences.feedback.section.showLineStability

  const handleStressToggle = () => {
    updatePreferences({
      feedback: {
        ...preferences.feedback,
        stress: {
          ...preferences.feedback.stress,
          enabled: !stressEnabled,
        },
      },
    })
  }

  const handleStressCountToggle = () => {
    updatePreferences({
      feedback: {
        ...preferences.feedback,
        stress: {
          ...preferences.feedback.stress,
          showStressCount: !stressCountEnabled,
        },
      },
    })
  }

  const handleRhymeLabelsToggle = () => {
    updatePreferences({
      feedback: {
        ...preferences.feedback,
        rhyme: {
          ...preferences.feedback.rhyme,
          showLabels: !rhymeLabelsEnabled,
        },
      },
    })
  }

  const handleSectionValidationToggle = () => {
    updatePreferences({
      feedback: {
        ...preferences.feedback,
        section: {
          ...preferences.feedback.section,
          showValidation: !sectionValidationEnabled,
        },
      },
    })
  }

  const handleLineStabilityToggle = () => {
    updatePreferences({
      feedback: {
        ...preferences.feedback,
        section: {
          ...preferences.feedback.section,
          showLineStability: !lineStabilityEnabled,
        },
      },
    })
  }

  // Collapsed state - radar icon only (the "AI Radar" brand)
  if (!isExpanded) {
    return (
      <div className="feedback-panel-collapsed inline-flex">
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="
            inline-flex items-center justify-center w-8 h-8 rounded-md
            bg-neutral-100 hover:bg-neutral-200 text-neutral-600
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
          "
          aria-label="Expand feedback panel"
          aria-expanded="false"
          title="AI Radar - Show feedback toggles"
          data-testid="feedback-panel-expand"
        >
          <RadarIcon className="w-5 h-5" />
        </button>
      </div>
    )
  }

  // Expanded state - toggles visible
  return (
    <div className="feedback-panel-wrapper">
      <div
        className="feedback-panel-expanded inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-neutral-50 border border-neutral-200"
        data-testid="feedback-panel"
      >
        {/* Collapse button - radar icon maintains brand identity */}
        <button
          type="button"
          onClick={() => setIsExpanded(false)}
          className="
            inline-flex items-center justify-center w-6 h-6 rounded
            hover:bg-neutral-200 text-neutral-500
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
          "
          aria-label="Collapse feedback panel"
          aria-expanded="true"
          title="AI Radar - Hide feedback toggles"
          data-testid="feedback-panel-collapse"
        >
          <RadarIcon className="w-4 h-4" />
        </button>

        {/* Divider */}
        <div className="w-px h-4 bg-neutral-200" />

        {/* Stress marks toggle - controls ALL stress mark visibility */}
        <CompactToggle
          enabled={stressEnabled}
          onToggle={handleStressToggle}
          label="Stress"
          icon={<StressMarkIcon />}
          testId="stress-marks-toggle"
        />

        {/* Stress count badge toggle - controls per-line stress count visibility */}
        <CompactToggle
          enabled={stressCountEnabled}
          onToggle={handleStressCountToggle}
          label="Count"
          icon={<StressCountIcon />}
          testId="stress-count-toggle"
        />

        {/* Rhyme labels toggle - controls rhyme scheme label visibility */}
        <CompactToggle
          enabled={rhymeLabelsEnabled}
          onToggle={handleRhymeLabelsToggle}
          label="Rhyme"
          icon={<RhymeLabelIcon />}
          testId="rhyme-labels-toggle"
        />

        {/* Legend toggle button - only shown when rhyme labels are enabled */}
        {rhymeLabelsEnabled && (
          <button
            type="button"
            onClick={() => setShowLegend(!showLegend)}
            className={`
              inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs
              transition-colors duration-200
              focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500
              ${showLegend
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-neutral-200 text-neutral-600 hover:bg-neutral-300'
              }
            `}
            aria-label={showLegend ? 'Hide rhyme legend' : 'Show rhyme legend'}
            aria-expanded={showLegend}
            data-testid="rhyme-legend-toggle-button"
            title={showLegend ? 'Hide category legend' : 'Show category legend'}
          >
            <span style={{ fontSize: '10px' }}>?</span>
            <span>Legend</span>
          </button>
        )}

        {/* Divider before section toggles */}
        <div className="w-px h-4 bg-neutral-200" />

        {/* Section validation toggle - controls section validation badge visibility */}
        <CompactToggle
          enabled={sectionValidationEnabled}
          onToggle={handleSectionValidationToggle}
          label="Sections"
          icon={<SectionValidationIcon />}
          testId="section-validation-toggle"
        />

        {/* Line stability toggle - controls line count parity indicator visibility */}
        <CompactToggle
          enabled={lineStabilityEnabled}
          onToggle={handleLineStabilityToggle}
          label="Stability"
          icon={<LineStabilityIcon />}
          testId="line-stability-toggle"
        />
      </div>

      {/* Rhyme legend - shown when rhyme labels enabled and legend toggle is on */}
      {rhymeLabelsEnabled && showLegend && (
        <RhymeLegend defaultExpanded={true} />
      )}
    </div>
  )
}
